import { ChevronRight } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { Transaction, MatchState } from './types'
import { formatCurrency } from './format'

const STATUS_CONFIG: Record<MatchState, { color: string; label: string }> = {
  committed: { color: 'bg-blue-500', label: 'Committed' },
  confirmed: { color: 'bg-green-500', label: 'Confirmed' },
  auto_confirmed: { color: 'bg-gray-400', label: 'Auto' },
  suggested: { color: 'bg-amber-500', label: 'Suggested' },
  unmatched: { color: 'bg-red-500', label: 'No match' },
  reversed: { color: 'bg-purple-500', label: 'Reversed' },
  rejected: { color: 'bg-neutral-400', label: 'Rejected' },
}

interface TransactionRowProps {
  transaction: Transaction
  isExpanded: boolean
  onToggle: () => void
  isSelected: boolean
  onToggleSelect: () => void
}

export function TransactionRow({ transaction, isExpanded, onToggle, isSelected, onToggleSelect }: TransactionRowProps) {
  const state: MatchState = transaction.match?.state ?? 'unmatched'
  const { color, label } = STATUS_CONFIG[state]

  return (
    <div
      onClick={onToggle}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-neutral-50',
        isExpanded && 'bg-neutral-50',
        isSelected && 'bg-blue-50/50'
      )}
    >
      <div
        onClick={(e) => {
          e.stopPropagation()
          onToggleSelect()
        }}
        className="flex h-4 w-4 shrink-0 items-center justify-center"
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect()}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select transaction"
        />
      </div>

      <ChevronRight
        className={cn(
          'h-4 w-4 shrink-0 text-neutral-400 transition-transform',
          isExpanded && 'rotate-90'
        )}
      />

      <span
        className={cn('h-2.5 w-2.5 shrink-0 rounded-full', color)}
        title={label}
      />

      <span className="w-[90px] shrink-0 text-neutral-500">
        {transaction.date}
      </span>

      <span className="min-w-0 flex-1 truncate font-medium text-neutral-900">
        {transaction.counterparty}
      </span>

      <span className="min-w-0 flex-1 truncate text-neutral-500">
        {transaction.description}
      </span>

      <span
        className={cn(
          'w-[120px] shrink-0 text-right font-mono font-medium',
          transaction.amount >= 0 ? 'text-green-700' : 'text-red-600'
        )}
      >
        {transaction.amount >= 0 ? '+' : ''}
        {formatCurrency(transaction.amount, transaction.currency)}
      </span>
    </div>
  )
}
